import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { httpInterceptor } from '../../../API/interceptor';
import { getNeighborhoods } from '../../../API/apiService';
import CustomFilterSelect from '../../../shared/customFilterSelect';
import './style.scss';

function getProgramScopeFromOrganizationalType(value, forcedScope = '') {
  const normalizedForcedScope = String(forcedScope || '').trim().toUpperCase();
  if (normalizedForcedScope === 'CS') return 'CS';
  const normalizedType = String(value || '').trim().toUpperCase();
  if (normalizedType === 'DIPLOMADO' || normalizedType === 'DIPLOMADOS') {
    return 'DIPLOM';
  }
  return normalizedType === 'IE' ? 'PF' : 'NDN';
}

function getUserDisplayName(user) {
  return String(user?.full_name ?? user?.fullName ?? user?.name ?? '').trim();
}

function getOptionName(options, optionId) {
  return (
    options.find(option => String(option?.id) === String(optionId))?.name || ''
  );
}

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function getInitialAddress(school) {
  const address = String(school?.address ?? '').trim();
  const schoolName = String(school?.name ?? school?.address ?? '').trim();

  if (!address) return '';
  if (schoolName && normalizeText(address) === normalizeText(schoolName)) return '';

  return address;
}

function CreateSchool({
  setOpenModal,
  schoolType,
  municipalities,
  users,
  created,
  mode = 'create',
  initialSchool = null,
  defaultProgramScope = 'PF'
}) {
  const isEditMode = mode === 'edit';
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [formData, setFormData] = useState({
    schoolId: '',
    code: '',
    name: '',
    address: '',
    municipality: '',
    neighborhood: '',
    type: '',
    programScope: defaultProgramScope,
    year: '',
    resourceType: '',
    organization: '',
    facilitator: ''
  });

  const toPositiveInt = (value) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  };

  const mediatorUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];

    const unique = new Map();
    users.forEach((user) => {
      if (!user?.id) return;

      const roleName = normalizeText(user.roleName);
      const isMediator = roleName.includes('mediad') && roleName !== 'mediador cop';
      const isSocialControlMediator = roleName.includes('control social');
      const requiresSocialControlMediator =
        String(defaultProgramScope || '').trim().toUpperCase() === 'CS';
      const isActive = user.active === undefined ? true : Boolean(user.active);
      const normalizedName = String(user.name || '').trim();

      if (
        !isMediator ||
        !isActive ||
        !normalizedName ||
        (requiresSocialControlMediator && !isSocialControlMediator)
      ) return;

      unique.set(String(user.id), {
        ...user,
        id: String(user.id),
        name: normalizedName
      });
    });

    return Array.from(unique.values()).sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), 'es', {
        sensitivity: 'base'
      })
    );
  }, [defaultProgramScope, users]);

  const currentSchoolId = String(initialSchool?.id ?? 'new');
  const initialMunicipalityId = toPositiveInt(
    initialSchool?.municipalityId ?? initialSchool?.municipality_id
  );
  const initialNeighborhoodId = toPositiveInt(
    initialSchool?.neighborhoodId ?? initialSchool?.neighborhood_id
  );
  const selectedTypeLabel = getOptionName(schoolType, formData.type);
  const selectedMunicipalityLabel =
    getOptionName(municipalities, formData.municipality) ||
    (String(formData.municipality) === String(initialMunicipalityId)
      ? String(initialSchool?.municipality?.name || '').trim()
      : '');
  const selectedNeighborhoodLabel =
    getOptionName(neighborhoods, formData.neighborhood) ||
    (String(formData.neighborhood) === String(initialNeighborhoodId)
      ? String(initialSchool?.neighborhood?.name || '').trim()
      : '');
  const selectedMediatorLabel =
    getOptionName(mediatorUsers, formData.facilitator) ||
    getUserDisplayName(initialSchool?.user);

  useEffect(() => {
    setFormData((prev) => {
      if (!prev.facilitator) return prev;

      const facilitatorExists = mediatorUsers.some(
        (user) => String(user.id) === String(prev.facilitator)
      );

      if (facilitatorExists) return prev;
      return { ...prev, facilitator: '' };
    });
  }, [mediatorUsers]);

  useEffect(() => {
    if (isEditMode && initialSchool) {
      const municipalityId = toPositiveInt(
        initialSchool?.municipalityId ?? initialSchool?.municipality_id
      );
      const neighborhoodId = toPositiveInt(
        initialSchool?.neighborhoodId ?? initialSchool?.neighborhood_id
      );
      const facilitatorId = toPositiveInt(
        initialSchool?.userId ?? initialSchool?.user_id ?? initialSchool?.user?.id
      );

      setSelectedMunicipality(municipalityId);
      setFormData({
        schoolId: initialSchool?.id ?? '',
        code: initialSchool?.code ?? '',
        name: initialSchool?.name ?? initialSchool?.address ?? '',
        address: getInitialAddress(initialSchool),
        municipality: municipalityId || '',
        neighborhood: neighborhoodId || '',
        type: initialSchool?.type ?? '',
        programScope:
          String(initialSchool?.programScope || '').trim().toUpperCase() ||
          getProgramScopeFromOrganizationalType(initialSchool?.type),
        year: initialSchool?.year ?? '',
        resourceType:
          initialSchool?.resourceTypeId ?? initialSchool?.resource_type_id ?? 'RO',
        organization: initialSchool?.organization ?? '',
        facilitator: facilitatorId ? String(facilitatorId) : ''
      });
      return;
    }

    setSelectedMunicipality(null);
    setNeighborhoods([]);
    setFormData({
      schoolId: '',
      code: '',
      name: '',
      address: '',
      municipality: '',
      neighborhood: '',
      type: '',
      programScope: defaultProgramScope,
      year: '',
      resourceType: '',
      organization: '',
      facilitator: ''
    });
  }, [isEditMode, initialSchool, defaultProgramScope]);

  useEffect(() => {
    if (!selectedMunicipality) {
      setNeighborhoods([]);
      return;
    }

    let ignore = false;

    async function loadNeighborhoods() {
      const response = await getNeighborhoods(selectedMunicipality);
      if (!ignore && response) {
        setNeighborhoods(response);
        if (Number(selectedMunicipality) === 99) {
          const cityNeighborhood = response.find(
            (neighborhood) => normalizeText(neighborhood?.name) === 'ciudad'
          );
          setFormData((prev) => ({
            ...prev,
            neighborhood: cityNeighborhood?.id || ''
          }));
        }
      }
    }

    loadNeighborhoods();

    return () => {
      ignore = true;
    };
  }, [selectedMunicipality]);

  function handleChange(event) {
    event.preventDefault();
    let { name, value } = event.target;

    if (name === 'name') {
      value = String(value || '').toUpperCase();
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function onSelectMunicipality(selectName, id) {
    const municipalityId = toPositiveInt(id);
    setSelectedMunicipality(municipalityId);
    setNeighborhoods([]);

    setFormData((prev) => ({
      ...prev,
      municipality: municipalityId || '',
      neighborhood: ''
    }));
  }

  function onSelectNeighborhood(selectName, id) {
    const neighborhoodId = toPositiveInt(id);
    setFormData((prev) => ({ ...prev, neighborhood: neighborhoodId || '' }));
  }

  function onSelectType(selectName, id) {
    setFormData((prev) => ({
      ...prev,
      type: id,
      programScope: getProgramScopeFromOrganizationalType(id, defaultProgramScope)
    }));
  }

  function addInfo(selectName, id) {
    if (!selectName) return;
    setFormData((prev) => ({ ...prev, [selectName]: id }));
  }

  async function submitSchool() {
    const municipalityId = toPositiveInt(formData.municipality);
    const neighborhoodId = toPositiveInt(formData.neighborhood);

    if (!municipalityId || !neighborhoodId) {
      toast('Por favor seleccione una Comuna y un Barrio valido', {
        type: 'warning'
      });
      return;
    }

    if (!formData.name) {
      toast('Por favor digite el nombre de la Entidad Aliada', {
        type: 'warning'
      });
      return;
    }

    if (!formData.type) {
      toast('Por favor seleccione la Forma Organizativa', {
        type: 'warning'
      });
      return;
    }

    if (!formData.facilitator) {
      toast('Por favor seleccione un mediador', {
        type: 'warning'
      });
      return;
    }

    const facilitatorValid = mediatorUsers.some(
      (user) => String(user.id) === String(formData.facilitator)
    );

    if (!facilitatorValid) {
      toast('Por favor seleccione un mediador valido', {
        type: 'warning'
      });
      return;
    }

    const payload = {
      schoolId: toPositiveInt(formData.schoolId),
      name: String(formData.name || '').trim(),
      address: String(formData.address || '').trim(),
      municipality: municipalityId,
      neighborhood: neighborhoodId,
      type: formData.type,
      programScope: getProgramScopeFromOrganizationalType(
        formData.type,
        defaultProgramScope
      ),
      organization: String(formData.organization || '').trim(),
      facilitator: Number(formData.facilitator),
      resourceType: formData.resourceType || 'RO',
      year: isEditMode
        ? formData.year
        : new Date().getFullYear().toString(),
      groups: [],
      active: isEditMode
        ? undefined
        : getProgramScopeFromOrganizationalType(
            formData.type,
            defaultProgramScope
          ) === 'NDN'
    };

    try {
      const response = isEditMode
        ? await httpInterceptor.put('schools', payload)
        : await httpInterceptor.post('schools', payload);

      if (response?.status === 200 || response?.success) {
        setOpenModal(false);
        created(true);
      }
    } catch (error) {
      if (!isEditMode && error?.response?.data?.message?.code === '23505') {
        toast('La Entidad Aliada ya existe', { type: 'error' });
        return;
      }

      toast(
        isEditMode ? 'Error actualizando Entidad Aliada' : 'Error creando Entidad Aliada',
        { type: 'error' }
      );
    }
  }

  return (
    <section className='main-create-container'>
      <p className='title'>
        {isEditMode ? 'EDITAR PROCESO FORMATIVO' : 'CREAR PROCESO FORMATIVO'}
      </p>

      <section className='create-container'>
        {isEditMode ? (
          <label className='label code-field'>
            <input
              placeholder='Codigo de Proceso Formativo'
              type='text'
              name='code'
              className='input-form'
              value={formData.code}
              readOnly
            />
          </label>
        ) : null}

        <label className='label'>
          <input
            placeholder='Nombre de la Entidad Aliada'
            type='text'
            name='name'
            className='input-form'
            value={formData.name}
            onChange={handleChange}
          />
        </label>

        <label className='label'>
          <input
            placeholder='Dirección'
            type='text'
            name='address'
            className='input-form'
            value={formData.address}
            onChange={handleChange}
          />
        </label>

        <CustomFilterSelect
          key={`type-${currentSchoolId}-${formData.type || 'none'}`}
          optionsList={schoolType}
          placeholder={'Forma Organizativa'}
          addInfo={onSelectType}
          valueEdit={selectedTypeLabel}
        />

        <div className='mediador-container'>
          <CustomFilterSelect
            key={`facilitator-${currentSchoolId}-${formData.facilitator || 'none'}`}
            optionsList={mediatorUsers}
            placeholder={'Nombre del mediador'}
            addInfo={addInfo}
            keyName='facilitator'
            valueEdit={selectedMediatorLabel}
          />
        </div>

        <div className='place-info-container'>
          <CustomFilterSelect
            key={`municipality-${currentSchoolId}-${formData.municipality || 'none'}`}
            placeholder={'Comuna'}
            optionsList={municipalities}
            addInfo={onSelectMunicipality}
            valueEdit={selectedMunicipalityLabel}
          />
          <CustomFilterSelect
            key={`neighborhood-${currentSchoolId}-${selectedMunicipality || 'none'}-${formData.neighborhood || 'none'}`}
            placeholder={'Barrios'}
            optionsList={neighborhoods}
            addInfo={onSelectNeighborhood}
            searchable={true}
            searchPlaceholder='Escribe para buscar barrio'
            disabled={!selectedMunicipality}
            valueEdit={selectedNeighborhoodLabel}
          />
        </div>

        <div className='motherOrg-container'>
          <label className='label'>
            <input
              placeholder='Colectivo Organizacion Social que apoya el proceso'
              type='text'
              name='organization'
              className='input-form'
              value={formData.organization}
              onChange={handleChange}
            />
          </label>
        </div>

        <div className='buttons-container'>
          <button className='blue-site-button create' onClick={submitSchool} type='button'>
            {isEditMode ? 'GUARDAR CAMBIOS' : 'CREAR'}
          </button>

          <button
            className='button-white-border-blue'
            onClick={() => setOpenModal(false)}
            type='button'
          >
            CANCELAR
          </button>
        </div>
      </section>
    </section>
  );
}

export default CreateSchool;
